<script setup>
import {
    computed, onMounted, onUpdated, ref, watch,
} from "vue";
import { useRoute } from "vue-router";
import AvailableActivitiesList from "@/components/AvailableActivitiesList.vue";
import SelectInput from "@/components/Select/SelectInput.vue";
import {
    formatRawDate,
    getEventsAmount,
    validateActivityName,
    validateDateTimes,
    validateEditDetails,
} from "@/components/methods/index.js";
import EventDates from "../../../../components/EventDates.vue";
import EventDetails from "../../../../components/EventDetails.vue";
import Modal from "../../../../components/Modal.vue";
import NewHolidays from "../../../../components/NewHolidays.vue";
import NewSummary from "../../../../components/NewSummary.vue";
import RadioInputGroup from "../../../../components/forms/RadioInputGroup.vue";
import Steps from "../../../../components/parts/Steps.vue";
import { useMainStore } from "../../../../stores/mainStore.js";
import { usePricingStore } from "../../../../stores/pricingStore";
import { useBookingSettingsStore } from "../../../../stores/bookingStore";
import BookingSettings from "../../../../components/BookingSettings.vue";
import TicketsAndPricingV2 from "../../../../components/TicketsAndPriceV2.vue";

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false,
    },
    sessionsList: {
        type: Array,
        default: () => [],
    },
    availableActivities: {
        type: Array,
        default: () => [],
    },
    selectedAvailableActivity: {
        type: Object,
        default() {
            return {
                eventType: "",
                ages: [],
                name: "",
                img: "",
                description: "",
                primaryCategory: [],
                secondaryCategories: [],
                bookingEmail: "",
                bookingType: "",
            };
        },
    },
});

const emit = defineEmits(["closeModal", "submitModal", "changeAvailableActivity"]);
const route = useRoute();

const mainStore = useMainStore();
const pricingStore = usePricingStore();
const bookingSettingsStore = useBookingSettingsStore();

// Reference definitions
const eventDetailsComponent = ref(null);
const step = ref(1);
const selectedActivityIdx = ref(null);
const listedEvent = ref("true");
const hasPreviousEvents = ref(true);
const resetCounter = ref(0);
const selectedAvailableActivity = ref(null);
const showAddNewEventSpinner = ref(false);
const eventDetails = ref({
    eventType: "",
    ages: [],
    name: "",
    img: "",
    description: "",
    primaryCategory: [],
    secondaryCategories: [],
    bookingEmail: "",
    bookingType: "",
});
const eventDetailsErrors = ref({});
const preselectedEventDetails = ref({});
const eventDates = ref({});
const eventDatesErrors = ref({
    dates: {
        start: {
            plain: null,
        },
        end: {
            plain: null,
        },
    },
    times: {
        start: null,
        end: null,
    },
});
const addHolidays = ref("");
const eventHolidays = ref([]);
const eventPricing = ref({
    freeTrial: "",
    priceData: {
        price: "",
    },
    freePricing: false,
});
const eventPricingErrors = ref({});
const preExistingHolidays = ref([]);
const eventDetailsActivityData = ref(false);

// Method definitions
const clearModal = () => {
    selectedAvailableActivity.value = null;
    step.value = 1;
    if (eventDetailsComponent.value !== null) {
        resetCounter.value += 1;
    }

    listedEvent.value = "true";
    eventDetailsErrors.value = {};

    eventDates.value = {};
    eventDatesErrors.value = {};

    eventPricing.value = {
        freeTrial: false,
        priceData: {
            price: "",
        },
        freePricing: false,
    };
    eventPricingErrors.value = {};
    eventHolidays.value = [];
    selectedActivityIdx.value = null;
    pricingStore.reset();
    bookingSettingsStore.reset();
};

const resetErrors = () => {
    eventDetailsErrors.value = {};
    eventDatesErrors.value = {
        dates: {
            start: {
                plain: null,
            },
            end: {
                plain: null,
            },
        },
        times: {
            start: null,
            end: null,
        },
    };
    eventPricingErrors.value = {};
};

const prevStep = () => {
    if (step.value > 1) {
        resetErrors();
        step.value -= 1;
    } else {
        emit("closeModal");
    }
};

const closeModal = () => {
    step.value = 1;
    clearModal();
    emit("closeModal");
};

const showHolidays = computed(() => {
    if (eventDates.value.repeatType === "no-repeat" || eventDates.value.repeatType === undefined) {
        return false;
    }
    if (eventDates.value.repeatType !== "no-repeat" && addHolidays.value === "yes") {
        return true;
    }
    return false;
});

// eslint-disable-next-line consistent-return
const nextStep = async () => {
    showAddNewEventSpinner.value = true;

    // if secondary category is array of objects convert it to object
    if (eventDetails.value.secondaryCategories
        && Array.isArray(eventDetails.value.secondaryCategories)) {
        const [secondaryCategories] = eventDetails.value.secondaryCategories;
        eventDetails.value.secondaryCategories = secondaryCategories;
    }
    // Details validation
    if (step.value === 1) {
        if (listedEvent.value === "true") {
            if (selectedActivityIdx.value === null) {
                showAddNewEventSpinner.value = false;
                return false;
            }
        } else {
            const errors = validateEditDetails(eventDetails.value);

            if (Object.values(errors).filter((el) => el !== "").length > 0) {
                eventDetailsErrors.value = errors;
                showAddNewEventSpinner.value = false;
                return false;
            }

            const response = await validateActivityName(eventDetails.value.name);

            if (eventDetails.value.primaryCategory === eventDetails.value.secondaryCategories) {
                eventDetails.value.secondaryCategories = null;
            }

            if (response.data === "false" && response.errors.name) {
                showAddNewEventSpinner.value = false;
                eventDetailsErrors.value.name = response.errors.name;
                return false;
            }
        }
    }
    // Date and time validation
    if (step.value === 2) {
        const errors = validateDateTimes(eventDates.value, addHolidays.value);
        if (Object.values(errors).filter((el) => el !== "").length > 0) {
            eventDatesErrors.value = errors;
            showAddNewEventSpinner.value = false;
            return false;
        }
    }
    /* Ticket selling validation */
    if ((step.value === 4 && showHolidays.value)
        || (step.value === 3 && !showHolidays.value)) {
        if (!pricingStore.validate()) {
            showAddNewEventSpinner.value = false;
            return false;
        }
    }
    /* Booking settings validation */
    if ((step.value === 5 && showHolidays.value && pricingStore.sellingTicket)
        || (step.value === 4 && !showHolidays.value && pricingStore.sellingTicket)) {
        if (!bookingSettingsStore.validate()) {
            showAddNewEventSpinner.value = false;
            return false;
        }
        bookingSettingsStore.setLocalStorage();
    }
    /*
    Available usecases:
    Case 1:
        1 - details
        2 - dates
        3 - pricing
        4 - summary
    Nexstep validation:
    step.value < 4 && !holiday && !pricingStore.sellingTicket

    Case 2:
        1 - details
        2 - dates
        3 - holidays
        4 - pricing
        5 - summary
    Nexstep validation:
    step.value < 5 && holiday && !pricingStore.sellingTicket

    Case 3:
        1 - details
        2 - dates
        3 - pricing
        4 - booking settings
        5 - summary
    Nexstep validation:
    step.value < 5 && !holiday && pricingStore.sellingTicket

    Case 4:
        1 - details
        2 - dates
        3 - holidays
        4 - pricing
        5 - booking settings
        6 - summary
    Nexstep validation:
    step.value < 6 && holiday && pricingStore.sellingTicket
*/

    const isBelowStep4WithoutHolidaysAndWithoutSellingTicket = step.value < 4
        && !showHolidays.value
        && !pricingStore.sellingTicket;
    const isBelowStep5WithHolidaysAndWithoutSellingTicket = step.value < 5
        && showHolidays.value
        && !pricingStore.sellingTicket;
    const isBelowStep5WithoutHolidaysAndWithSellingTicket = step.value < 5
        && !showHolidays.value
        && pricingStore.sellingTicket;
    const isBelowStep6WithHolidaysAndWithSellingTicket = step.value < 6
        && showHolidays.value
        && pricingStore.sellingTicket;

    if (isBelowStep4WithoutHolidaysAndWithoutSellingTicket
        || isBelowStep5WithHolidaysAndWithoutSellingTicket
        || isBelowStep5WithoutHolidaysAndWithSellingTicket
        || isBelowStep6WithHolidaysAndWithSellingTicket) {
        step.value += 1;
    } else {
        await mainStore.addNewEventForSimpleFlow({
            eventDetails: listedEvent.value === "true" ? preselectedEventDetails.value : eventDetails.value,
            eventDates: eventDates.value,
            eventHolidays: eventHolidays.value,
            eventPricing: eventPricing.value,
            venueId: route.params.venueId,
        });

        closeModal();
        emit("submitModal");
        bookingSettingsStore.removeLocalStorage();
    }
    showAddNewEventSpinner.value = false;
};

const handleUpdateEventDetails = (data) => {
    eventDetails.value = data;
};

const handleUpdateEventDates = (data) => {
    eventDates.value = data;
};

const handleUpdateHolidays = (data) => {
    eventHolidays.value = data;
};

const handleUpdatePricing = (data) => {
    eventPricing.value = data;
};

const getRawHolidays = () => {
    if (Object.values(eventHolidays.value).length === 0) {
        return [];
    }

    const eventHolsArray = eventHolidays.value;

    if (eventHolsArray == null) { // if null/undefined assign empty array
        return [];
    }

    return eventHolsArray.map((h) => {
        const start = new Date(h.start.plain);
        const end = (h.oneDay === true) ? start : new Date(h.end.plain);

        return {
            start: formatRawDate(start),
            end: formatRawDate(end),
            oneDay: h.oneDay,
        };
    });
};

const edit = (editType) => {
    if (editType === "details") {
        step.value = 1;
    }

    if (editType === "date-time") {
        step.value = 2;
    }

    if (editType === "holidays") {
        step.value = 3;
    }

    if (editType === "price") {
        if (addHolidays.value === "yes") {
            step.value = 4;
        } else {
            step.value = 3;
        }
    }

    // TODO: add booking settings step
};

const getAllEventsAmount = computed(() => getEventsAmount(
    eventDates.value?.dates?.start?.plain,
    eventDates.value?.dates?.end?.plain,
    eventDates.value.repeatType,
    eventHolidays.value,
));

const getEventsCount = () => {
    const res = getAllEventsAmount.value;

    const eventType = listedEvent.value === "true"
        ? preselectedEventDetails.value.eventType
        : (eventDetails.value.eventType?.name ?? eventDetails.value.eventType);

    const text = res === 1 ? "date" : "dates";

    return `${res} ${eventType?.toLowerCase()} ${text}`;
};

const getCoursePriceLabel = () => {
    if (eventDates.value.repeatType === "day") {
        return `Price for all ${getEventsCount()} (optional)`;
    }
    return "Price for multiple sessions (optional)";
};

const getCoursePriceText = () => `For all ${getEventsCount()}`;

const getPreExistingHolidays = () => {
    const formData = new FormData();

    formData.append("userId", window.currentUserId);
    formData.append("startDate", JSON.stringify(eventDates.value?.dates?.start));
    formData.append("endDate", JSON.stringify(eventDates.value?.dates.end));

    fetch("/get-existing-holidays", {
        method: "POST",
        body: formData,
    }).then((res) => res.json()).then((data) => {
        if (data.success) {
            // console.log(data)
            preExistingHolidays.value = data.data;
        } else {
            alert("Form submitted with errors");
        }
    });
};

const totalSteps = computed(() => {
    if (pricingStore.sellingTicket) {
        return showHolidays.value ? 6 : 5;
    }
    return showHolidays.value ? 5 : 4;
});

const isSummaryStepActive = computed(() => {
    if (pricingStore.sellingTicket) {
        return (step.value === 6 && showHolidays.value)
            || (step.value === 5 && !showHolidays.value);
    }
    return (step.value === 5 && showHolidays.value) || (step.value === 4 && !showHolidays.value);
});

const isBookingSettingsStepActive = computed(() => {
    if (pricingStore.sellingTicket) {
        return (step.value === 5 && showHolidays.value)
            || (step.value === 4 && !showHolidays.value);
    }
    return false;
});

const getEventType = computed(() => (listedEvent.value === "true"
    ? preselectedEventDetails.value.eventType
    : (eventDetails.value.eventType?.name ?? eventDetails.value.eventType)));

onMounted(async () => {
    if (props.sessionsList.length === 0) {
        listedEvent.value = "false";
        hasPreviousEvents.value = false;
    }

    pricingStore.reset();
});

onUpdated(() => {
    if (
        (eventDetailsComponent.value !== null)
        && (selectedAvailableActivity.value || listedEvent.value === "true")
    ) {
        eventDetailsActivityData.value = props.selectedAvailableActivity;
        // eventDetailsComponent.value.setActivityData(props.selectedAvailableActivity);
    }
});

watch(selectedAvailableActivity, () => {
    emit("changeAvailableActivity", selectedAvailableActivity.value);
});

watch(selectedActivityIdx, (current) => {
    preselectedEventDetails.value = props.sessionsList[current];
});

watch(
    () => step.value,
    () => {
        if (step.value === 3 && showHolidays.value) {
            getPreExistingHolidays();
        }
    },
);
</script>

<template>
    <Modal
        v-if="props.showModal"
        :show-modal="$props.showModal"
        :default-footer="step === 1"
        :close-text="step === 1 ? 'Close' : 'Back'"
        :submit-text="isSummaryStepActive ? 'Save and publish' : 'Next'"
        :close-btn-class="step === 1 ? '' : 'btn-new btn-new-outline ml-0'"
        :show-spinner="showAddNewEventSpinner"
        @close-modal="closeModal"
        @submit-delete="prevStep"
        @submit-modal="nextStep"
        :test-id-submit-btn="'sim-add-modal-submit-btn'"
    >
        <Steps
            :total-steps="totalSteps"
            :current-step="step"
            block-class=""
            :show-step-items="false"
            class="mb-3"
        />

        <div v-show="step === 1">
            <h1>Event details</h1>

            <div data-test="radio-group-copy-or-new-event">
                <div v-if="hasPreviousEvents" class="small-title mb-5">Have you already listed this event?</div>

                <RadioInputGroup
                    v-if="hasPreviousEvents"
                    label="title"
                    v-model="listedEvent"
                    :selected="listedEvent || ''"
                    :list="[
                        {
                            title: 'Yes - I want to add a new date and time to an '
                                + 'event I’ve already created',
                            dataTest: 'radio-copy-exist-event',
                            value: 'true',
                        },
                        {
                            title: 'No - I want to create a new event',
                            dataTest: 'radio-create-new-event',
                            value: 'false',
                        }
                    ]"
                />
            </div>

            <div v-show="listedEvent === 'true'" class="your-event-gap">
                <div class="small-title mb-1">Your events</div>

                <div class="small-subtitle">Select the event you would you like to add a new date and time to.</div>

                <div class="listed-event-container">
                    <AvailableActivitiesList
                        :list="$props.sessionsList"
                        :selected-idx="selectedActivityIdx"
                        @idx-change="selectedActivityIdx = $event"
                    />
                </div>
            </div>

            <div v-show="listedEvent !== 'true'">

                <div class="d-flex flex-column select-container your-event-gap mb-3">
                    <label class="show">Copy event details (optional)</label>
                    <div class="small-subtitle">To save time, you can copy details from an event you’ve already
                        created.
                    </div>
                    <SelectInput
                        class="available-activities"
                        :options="$props.availableActivities"
                        label="title"
                        placeholder="Choose an event"
                        :multiple="false"
                        :default="selectedAvailableActivity || null"
                        @change="selectedAvailableActivity = $event"
                    />
                </div>
                <EventDetails
                    ref="eventDetailsComponent"
                    :default-categories="mainStore.allCategories"
                    :default="{
                        bookingEmail: mainStore.defaultEmailForBusiness,
                    }"
                    :set-activity-data="eventDetailsActivityData"
                    :errors="eventDetailsErrors"
                    @updated="handleUpdateEventDetails"
                    @set-img="eventDetails.img = $event"
                    :reset="resetCounter"
                />
            </div>
        </div>

        <div v-show="step === 2">
            <EventDates
                :event-type="listedEvent === 'true'
                    ? preselectedEventDetails.eventType || ''
                    : eventDetails.eventType || ''"
                :default-dates="{
                    start: '',
                    end: '',
                }"
                :default-times="{
                    start: '',
                    end: '',
                }"
                :errors="eventDatesErrors"
                @updated="handleUpdateEventDates"
            />

            <div v-if="eventDates.repeatType !== 'no-repeat'" data-test="block-add-holiday">
                <div class="small-title holidays-spacing mt-5">
                    Would you like to add a holiday?
                </div>
                <div
                    v-if="eventDates.repeatType !== 'no-repeat'"
                    class="d-flex flex-column"
                >
                    <div class="mt-2" data-test-id="man-pan-sim-add-holiday">
                        <RadioInputGroup
                            label="title"
                            v-model="addHolidays"
                            id="-add-holidays"
                            :selected="addHolidays"
                            :list="[
                                {
                                    title: 'Yes',
                                    value: 'yes',
                                    dataTest: 'radio-holiday-yes',
                                },
                                {
                                    title: 'No',
                                    value: 'no',
                                    dataTest: 'radio-holiday-no',
                                },
                            ]"
                        />
                    </div>
                </div>
                <div :class="{'error-text': eventDatesErrors.addHoliday !== ''}">
                    {{ eventDatesErrors.addHoliday }}
                </div>
            </div>
        </div>

        <div v-show="step === 3 && showHolidays">
            <h1 class="holidays-gap">Holidays</h1>

            <NewHolidays
                :start-date="eventDates.dates?.start?.plain"
                :end-date="eventDates.dates?.end?.plain"
                :existing-holidays="preExistingHolidays"
                @updated="handleUpdateHolidays"
            />
        </div>

        <div v-show="(step === 4 && showHolidays) || (step === 3 && !showHolidays)">
            <TicketsAndPricingV2
                :event-type="getEventType"
                :recurring-type="eventDates.repeatType"
                :all-events-amount="getAllEventsAmount"
            />
        </div>

        <div v-if="isBookingSettingsStepActive">
            <BookingSettings/>
        </div>

        <div v-if="isSummaryStepActive">
            <h1 class="mb-1">Summary</h1>

            <div class="events-amount">{{ getEventsCount() }}</div>

            <NewSummary
                :activity="{
                    eventType: listedEvent === 'true'
                        ? preselectedEventDetails.eventType
                        : (eventDetails.eventType?.name ?? eventDetails.eventType),
                    description: listedEvent === 'true'
                        ? preselectedEventDetails.description
                        : eventDetails.description,
                    bookingEmail: listedEvent === 'true'
                        ? preselectedEventDetails.bookingEmail
                        : eventDetails.bookingEmail,
                    bookingType: listedEvent === 'true'
                        ? preselectedEventDetails.bookingType
                        : eventDetails.bookingType,
                    bookingLink: listedEvent === 'true'
                        ? preselectedEventDetails.bookingLink
                        : eventDetails.bookingLink,
                    name: listedEvent === 'true'
                        ? preselectedEventDetails.name
                        : eventDetails.name
                }"
                :mainCategory="listedEvent === 'true'
                    ? preselectedEventDetails.primaryCategory
                    :eventDetails.primaryCategory?.name"
                :secondaryCategory="listedEvent === 'true'
                    ? preselectedEventDetails.secondaryCategories
                    : eventDetails.secondaryCategories?.name"
                :ages="listedEvent === 'true'
                    ? preselectedEventDetails.ages
                    :eventDetails?.ages?.map((a) => a.name).join(', ')"
                :img="listedEvent === 'true' ? preselectedEventDetails.img :eventDetails.img"
                :dates="{
                    start: formatRawDate(eventDates?.dates?.start?.plain) ?? '',
                    end: formatRawDate(eventDates?.dates?.end?.plain) ?? '',
                    startRaw: eventDates?.dates?.start?.plain,
                    endRaw: eventDates?.dates?.end?.plain,
                    repeat: eventDates.repeatType
                }"
                :times="{
                    start: eventDates.times.start,
                    end: eventDates.times.end,
                }"
                :force-show-holidays="addHolidays === 'yes'"
                :holidays="getRawHolidays()"
                :addHoliday="getRawHolidays().length > 0 ? 'yes' : 'no'"
                :price="{
                    price: eventPricing.priceData.price,
                    pricePerTermLabel: getCoursePriceText(),
                    pricePerTerm: eventPricing.priceData.pricePerTerm === '0.00'
                        ? ''
                        : eventPricing.priceData.pricePerTerm,
                    freeTrial: eventPricing.freeTrial === 'yes' ? 'Yes' : 'No',
                    freePricing: eventPricing.freePricing,
                }"
                :priceV2="pricingStore.getPricingState"
                @edit-click="edit($event)"
            />
        </div>
    </Modal>
</template>

<style scoped>
h1 {
    font-style: normal;
    font-weight: 600;
    font-size: 2rem;
    line-height: 48px;
    margin-bottom: 2rem;
}

.card-container {
    box-sizing: border-box;
    width: 100%;
    min-height: 85px;
    background: #FFFFFF;
    padding: 16px 18px;
    box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
    border-radius: 15px;
}

.card-container.active-card {
    border: 2px solid #FFB700;
}

.listed-event-container {
    height: 186px;
    padding-right: 8px;
    padding-left: 2px;
    overflow-y: scroll;
}

.events-amount {
    font-style: normal;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.3125rem;
    color: #636066;
    margin-bottom: 3rem;
}

.your-event-gap {
    margin-top: 2.1rem;
}

.holidays-gap {
    margin-bottom: 2.5rem;
}
</style>
